import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { APP_NAME, AUFGABEN_PRO_RUNDE } from '../config'
import { serieBerechnen } from '../speicher/serie'
import type { Serienstand, Tagesrunde } from '../speicher/typen'
import { speicher } from '../useUebung'
import { heute as heuteIso, plusTage } from '../scheduler/datum'
import { Maskottchen } from '../spiel/Maskottchen'
import { Sterne } from '../spiel/Deko'
import { tonAufwecken } from '../ton'
import { tagesstandHolen, zugangLesen, type Tagesstand } from '../raum/api'
import { reiseStand } from '../raum/reise'

export function Start() {
  const [serie, setSerie] = useState<Serienstand | null>(null)
  const [runden, setRunden] = useState<Tagesrunde[]>([])
  const [raum, setRaum] = useState<Tagesstand | null>(null)
  const navigate = useNavigate()
  const heute = heuteIso()

  useEffect(() => {
    void (async () => {
      const geladen = await speicher.rundenLaden()
      setRunden(geladen)
      setSerie(serieBerechnen(geladen, heute))
    })()
  }, [heute])

  // Der Raum wird getrennt geladen und darf scheitern, ohne dass die
  // Startseite darunter leidet. Ohne Netz gibt es eben keine Kachel, aber
  // der Knopf zum Ueben funktioniert unveraendert.
  useEffect(() => {
    if (!zugangLesen()) return
    void tagesstandHolen()
      .then(setRaum)
      .catch(() => setRaum(null))
  }, [])

  function losgehen() {
    // Der Audiokontext darf nur aus einer Berührung heraus starten, sonst
    // sperren ihn die Autoplay-Regeln und die ganze Runde bleibt stumm.
    tonAufwecken()
    navigate('/uebung')
  }

  return (
    <div className="buehne">
      <Sterne />

      <div className="mitte">
        <Maskottchen stimmung={serie && serie.laenge > 0 ? 'begeistert' : 'neutral'} gross />

        <h1 className="titel">{APP_NAME}</h1>
        <p className="unterzeile">{begruessung(serie)}</p>

        {serie && <Serienkalender runden={runden} heute={heute} />}

        {raum && <Raumkachel stand={raum} />}

        <button className="knopf" onClick={losgehen} type="button">
          Losgehen
        </button>

        <Link className="knopf knopf--sternental" to="/herde">
          Ins Sternental
        </Link>

        <div className="zeile" style={{ marginTop: '5vmin' }}>
          <Link className="knopf knopf--leise" to={raum ? '/raum' : '/raum/beitreten'}>
            {raum ? 'Unser Raum' : 'Mit Freundinnen üben'}
          </Link>
          <Link className="knopf knopf--leise" to="/tv">
            Am Fernseher spielen
          </Link>
          <Link className="knopf knopf--leise" to="/einstellungen">
            Einstellungen
          </Link>
        </div>
      </div>
    </div>
  )
}

function begruessung(serie: Serienstand | null): string {
  if (!serie) return 'Einen Moment …'
  if (serie.heuteGeschafft) {
    return `Heute schon geschafft. Noch eine Runde? ${AUFGABEN_PRO_RUNDE} Aufgaben, das war's.`
  }
  if (serie.laenge === 0) {
    return `${AUFGABEN_PRO_RUNDE} Aufgaben, dann bist du fertig. Los geht's!`
  }
  if (serie.laenge === 1) return 'Gestern hast du geübt. Machst du heute weiter?'
  return `${serie.laenge} Tage am Stück! Heute weiter?`
}

/**
 * Die letzten 14 Tage als Haekchenreihe.
 *
 * Der Wochenjoker wird eigens markiert. Ein Kind soll sehen, dass der Tag
 * fehlt, aber eben auch, dass die Serie trotzdem haelt. Eine Luecke, die
 * unkommentiert bleibt, wirkt wie ein Fehler des Programms.
 */
function Serienkalender({ runden, heute }: { runden: Tagesrunde[]; heute: string }) {
  const geschafft = new Set(runden.filter((r) => r.geschafft).map((r) => r.datum))
  const tage = Array.from({ length: 14 }, (_, i) => plusTage(heute, i - 13))
  const serie = serieBerechnen(runden, heute)

  // Wie weit die aktuelle Serie zurueckreicht. Luecken davor sind einfach
  // Vergangenheit, Luecken darin sind verbrauchte Joker.
  const serienStart = serie.laenge > 0 ? fruehesterSerientag(geschafft, heute, serie.laenge) : null

  return (
    <div className="kalender">
      {tage.map((tag) => {
        const klassen = ['tag']
        if (geschafft.has(tag)) klassen.push('tag--geschafft')
        else if (serienStart && tag >= serienStart && tag < heute) klassen.push('tag--joker')
        if (tag === heute) klassen.push('tag--heute')

        return (
          <span key={tag} className={klassen.join(' ')} title={tag}>
            {geschafft.has(tag) ? '✓' : ''}
          </span>
        )
      })}
    </div>
  )
}

function fruehesterSerientag(geschafft: Set<string>, heute: string, laenge: number): string {
  let tag = heute
  let gezaehlt = 0
  for (let i = 0; i < 400 && gezaehlt < laenge; i++) {
    if (geschafft.has(tag)) gezaehlt++
    if (gezaehlt < laenge) tag = plusTage(tag, -1)
  }
  return tag
}

/**
 * Anriss des Raums auf der Startseite.
 *
 * Zeigt genau zwei Dinge: wie weit die Gruppe noch bis zur naechsten Station
 * hat, und ob das Tagesraetsel schon offen ist. Mehr wuerde vom einen grossen
 * Knopf ablenken, um den es hier geht.
 */
function Raumkachel({ stand }: { stand: Tagesstand }) {
  const navigate = useNavigate()
  const reise = reiseStand(stand.reise.rundenGesamt)

  return (
    <button className="raumkachel" onClick={() => navigate('/raum')} type="button">
      <div className="raumkachel__titel">
        {reise.amZiel ? `${reise.station.name} erreicht!` : `Unterwegs nach ${reise.naechste?.name}`}
      </div>
      <div className="raumkachel__zeile">
        {reise.amZiel
          ? 'Ihr habt die ganze Reise geschafft.'
          : `Noch ${reise.nochRunden === 1 ? 'eine Runde' : `${reise.nochRunden} Runden`}, dann seid ihr da.`}
      </div>
      <div className="raumkachel__zeile">
        {stand.raetsel.vollAufgedeckt
          ? 'Die Scherzfrage von heute ist aufgelöst.'
          : `Scherzfrage: ${stand.raetsel.geschafftHeute} von ${stand.raetsel.mitgliederAnzahl} haben geübt.`}
      </div>
    </button>
  )
}
